import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'wouter';
import { CustomerModal } from '@/components/customers/customer-modal';
import { BatterySerialNumbers } from './battery-serial-numbers';
import { DocumentUpload } from './document-upload';
import { DocumentViewer } from './document-viewer';
import DirectUpdateButton from './direct-update-button';
import { insertInstallationSchema, Installation, Customer } from '@shared/schema';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  FileText,
  Cpu,
  Construction,
  CalendarIcon,
  ArrowLeft,
  Check,
  ChevronsUpDown,
  Search,
  CheckCircle2,
  ImageIcon,
  X,
  FileArchive,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  Info,
  Banknote,
  FileCheck,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, cn } from '@/lib/utils';
import { queryClient } from '@/lib/queryClient';

// Define props interface for the form component
interface InstallationFormProps {
  installationId?: number;
  proposalId?: number;
  customerId?: number;
}

// Single-click checklist item component
const ChecklistItemField = ({
  control,
  name,
  label,
}: {
  control: any;
  name: string;
  label: string;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-0">
          <div className="flex items-center justify-between p-3 border rounded-lg bg-card">
            <FormLabel className="text-sm font-medium mt-0 flex-1">{label}</FormLabel>
            <div className="flex gap-2 items-center">
              <div
                className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${field.value === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-green-50'}`}
                onClick={() => field.onChange('completed')}
                title="Mark as completed"
              >
                <CheckCircle
                  className={`h-5 w-5 ${field.value === 'completed' ? 'opacity-100' : 'opacity-70'}`}
                />
              </div>

              <div
                className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${field.value === 'in_progress' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 hover:bg-blue-50'}`}
                onClick={() => field.onChange('in_progress')}
                title="Mark as in progress"
              >
                <Clock
                  className={`h-5 w-5 ${field.value === 'in_progress' ? 'opacity-100' : 'opacity-70'}`}
                />
              </div>

              <div
                className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${field.value === 'na' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400 hover:bg-red-50'}`}
                onClick={() => field.onChange('na')}
                title="Mark as not applicable"
              >
                <XCircle
                  className={`h-5 w-5 ${field.value === 'na' ? 'opacity-100' : 'opacity-70'}`}
                />
              </div>
            </div>
          </div>
        </FormItem>
      )}
    />
  );
};

// Create proper validation schema that extends the base installation schema
const installationFormSchema = insertInstallationSchema.partial().extend({
  // Override timestamp fields to accept Date objects from form
  contractSigned: z.date().nullable().optional(),
  scheduledDate: z.date().nullable().optional(),
  completedDate: z.date().nullable().optional(),
  depositPaid: z.date().nullable().optional(),
  sevenDayPaymentDate: z.date().nullable().optional(),
  finalPaymentDate: z.date().nullable().optional(),
  additionalPaymentDate: z.date().nullable().optional(),
  dnoApplicationSent: z.date().nullable().optional(),
  dnoApproved: z.date().nullable().optional(),
  commissioningDate: z.date().nullable().optional(),

  // Document arrays with proper validation
  financeDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  dnoDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  onsiteDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  postInstallDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  handoverDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  maintainingDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  technicalDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  contractDocuments: z
    .array(
      z.object({
        data: z.string(),
        name: z.string(),
        type: z.string(),
        size: z.number(),
      })
    )
    .default([]),

  // DNO fees validation
  dnoFees: z
    .array(
      z.object({
        description: z.string(),
        amount: z.string(),
      })
    )
    .default([]),

  // Additional fields that may be in form but not base schema
  customerEmail: z.string().nullable().optional(),
  customerPhone: z.string().nullable().optional(),
  installationAddress: z.string().nullable().optional(),
});

// Use proper type inference from the extended schema
type FormValues = z.infer<typeof installationFormSchema>;

export default function TabbedInstallationForm({
  installationId,
  proposalId: initialProposalId,
  customerId: initialCustomerId,
}: InstallationFormProps) {
  const { toast } = useToast();
  const isEditing = !!installationId;
  const [activeTab, setActiveTab] = useState('details');

  // State to store selected customer information for display
  const [selectedCustomerData, setSelectedCustomerData] = useState<{
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  } | null>(null);

  // Initialize from session storage if available
  useEffect(() => {
    // Try to get from session storage first
    const storedData = sessionStorage.getItem(`selected_customer_data`);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setSelectedCustomerData(parsedData);
        console.log('DOMELEC DEBUG: Loaded customer data from session storage:', parsedData);
      } catch (e) {
        console.error('DOMELEC DEBUG: Error parsing stored customer data:', e);
      }
    }
  }, []);

  // Form data persistence key
  const formDataKey = `installation_form_data_${installationId || 'new'}`;

  // Form setup with zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(installationFormSchema),
    defaultValues: {
      // Basic information
      customerId: initialCustomerId || 0,
      proposalId: initialProposalId || 0,
      status: 'pending',
      notes: null,
      installationAddress: null,
      customerEmail: null,
      customerPhone: null,

      // System information
      systemType: null,
      openSolarReference: null,
      purchaseOrderNumber: null,
      cost: null,

      // Date tracking
      contractSigned: null,
      scheduledDate: null,
      completedDate: null,

      // Payment tracking
      installationCost: null,
      depositAmount: null,
      depositPaid: null,
      sevenDayPaymentAmount: null,
      sevenDayPaymentDate: null,
      finalPaymentAmount: null,
      finalPaymentDate: null,
      additionalPaymentAmount: null,
      additionalPaymentDate: null,

      // Finance documents
      financeDocuments: [],

      // DNO Application details
      dnoProvider: null,
      dnoApplicationType: null,
      dnoApplicationSent: null,
      dnoSystemReference: null,
      dnoCaseReference: null,
      dnoApproved: null,
      fastTrackReference: null,
      dnoExportLimitation: null,
      dnoDeclaredNetCapacity: null,
      dnoFees: [],
      dnoAdditionalComments: null,

      // DNO documents
      dnoDocuments: [],

      // Installation Checklist Items - all default to 'pending'
      checklistBookScaffolding: 'pending',
      checklistLetterScaffoldInstallDate: 'pending',
      checklistPvPlannerContractors: 'pending',
      checklistScaffoldRemoved: 'pending',
      checklistExportMpanReceived: 'pending',
      checklistSwitchExportConnect: 'pending',
      checklistLaminateSchematic: 'pending',
      checklistLaminateStartupShutdown: 'pending',
      checklistF17ContractManagement: 'pending',
      checklistConsolidatedForm: 'pending',
      checklistDnoCommissioningSent: 'pending',
      checklistEicCompleted: 'pending',
      checklistStructuralSurvey: 'pending',
      checklistShadeReport: 'pending',
      checklistSolarPvDesignCalculations: 'pending',

      // On-site documents
      onsiteDocuments: [],

      // Post Installation Checklist
      checklistSendSunsynkApp: 'pending',
      checklistAmendmentsProposalInvoice: 'pending',
      checklistHiesReturnCompleted: 'pending',
      checklistPartPCertificateCreated: 'pending',
      checklistMcsCertification: 'pending',
      checklistFinancePhoenixPortal: 'pending',
      checklistInverterSerialToStock: 'pending',

      // Post-installation documents
      postInstallDocuments: [],

      // Handover Documents Checklist
      checklistFinalPaymentReceived: 'pending',
      checklistMcsCertificateProvided: 'pending',
      checklistEicCertificateProvided: 'pending',
      checklistPartPBuildingRegsCertificate: 'pending',
      checklistOwnerManual: 'pending',
      checklistStructuralReport: 'pending',
      checklistProposalUpdated: 'pending',
      checklistSchematic: 'pending',
      checklistSolarPanelDetails: 'pending',
      checklistSolarPvFormsConsolidated: 'pending',
      checklistInverterWarranty: 'pending',
      checklistFinalInvoice: 'pending',
      checklistCompleteOpenSolar: 'pending',

      // Handover documents
      handoverDocuments: [],

      // Maintaining Checklist
      checklistCustomerSatisfactionSurvey: 'pending',
      checklistDiariseYearCheck: 'pending',
      checklistAddSerialNumbersStock: 'pending',

      // Maintaining documents
      maintainingDocuments: [],

      // Technical details
      mpanNumber: null,
      exportMpan: null,
      inverterSerialNumber: null,
      batterySerialNumber: null,
      commissioningDate: null,

      // Technical documents
      technicalDocuments: [],

      // Scaffold Images
      scaffoldImageUrl1: null,
      scaffoldImageUrl2: null,
      scaffoldImageUrl3: null,
      scaffoldImageNotes: null,

      // Contract documents
      contractDocuments: [],

      // Other info
      installerNames: null,
    },
  });

  // Clear saved form data on successful submission
  const clearSavedFormData = () => {
    sessionStorage.removeItem(formDataKey);
  };

  // Load form data from session storage on component mount (only for new installations)
  useEffect(() => {
    if (!isEditing) {
      const savedFormData = sessionStorage.getItem(formDataKey);
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData);
          console.log('Loading saved form data from session storage:', parsedData);
          // Reset form with saved data
          form.reset(parsedData);
        } catch (e) {
          console.error('Error loading saved form data:', e);
        }
      }
    }
  }, [formDataKey, isEditing, form]);

  // Save form data to session storage whenever form values change (for both new and existing)
  useEffect(() => {
    const subscription = form.watch((value) => {
      // Save current form state to session storage
      sessionStorage.setItem(formDataKey, JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [form, formDataKey]);

  // Enhanced tab change handler that preserves form data
  const handleTabChange = (newTab: string) => {
    // Force form validation of current fields before changing tabs
    const currentValues = form.getValues();

    // Save current form state (for both new and existing installations)
    sessionStorage.setItem(formDataKey, JSON.stringify(currentValues));

    console.log('Switching tabs - current form values:', currentValues);
    setActiveTab(newTab);
  };

  // Form section navigation component
  const TabNavigation = () => {
    // Define tab order for form navigation
    const tabOrder = ['details', 'schedule', 'dno', 'scaffold', 'checklist', 'technical', 'notes'];

    // Get current tab index and calculate previous/next tabs
    const currentTabIndex = tabOrder.indexOf(activeTab);
    const prevTab = currentTabIndex > 0 ? tabOrder[currentTabIndex - 1] : null;
    const nextTab = currentTabIndex < tabOrder.length - 1 ? tabOrder[currentTabIndex + 1] : null;

    // Get form section labels for display
    const getTabLabel = (tabValue: string) => {
      switch (tabValue) {
        case 'details':
          return 'Basic Details';
        case 'schedule':
          return 'Schedule & Payment';
        case 'dno':
          return 'DNO Application';
        case 'scaffold':
          return 'Scaffold Images';
        case 'checklist':
          return 'Installation Checklist';
        case 'technical':
          return 'Technical Details';
        case 'notes':
          return 'Additional Notes';
        default:
          return tabValue;
      }
    };

    // Get form section icon component
    const getTabIcon = (tabValue: string) => {
      switch (tabValue) {
        case 'details':
          return <Info className="h-4 w-4" />;
        case 'schedule':
          return <CalendarIcon className="h-4 w-4" />;
        case 'dno':
          return <Construction className="h-4 w-4" />; // Changed to Construction icon
        case 'scaffold':
          return <ImageIcon className="h-4 w-4" />;
        case 'checklist':
          return <ClipboardCheck className="h-4 w-4" />;
        case 'technical':
          return <Cpu className="h-4 w-4" />;
        case 'notes':
          return <FileText className="h-4 w-4" />;
        default:
          return <Info className="h-4 w-4" />;
      }
    };

    return (
      <div className="flex items-center justify-between pt-6 pb-2 border-t mt-8">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setActiveTab(tabOrder[0])}
            disabled={currentTabIndex === 0}
          >
            <ChevronsLeft className="h-4 w-4 mr-1" />
            First
          </Button>

          {prevTab && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setActiveTab(prevTab)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {getTabLabel(prevTab)}
            </Button>
          )}
        </div>

        <div className="text-sm text-muted-foreground hidden md:block">
          {currentTabIndex + 1} of {tabOrder.length}
        </div>

        <div className="flex items-center gap-1">
          {nextTab && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setActiveTab(nextTab)}
            >
              {getTabLabel(nextTab)}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setActiveTab(tabOrder[tabOrder.length - 1])}
            disabled={currentTabIndex === tabOrder.length - 1}
          >
            Last
            <ChevronsRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  // Initialize date fields for form state management
  const [dates, setDates] = useState({
    // Date tracking
    contractSigned: form.getValues().contractSigned,
    scheduledDate: form.getValues().scheduledDate,
    completedDate: form.getValues().completedDate,

    // Payment date tracking
    depositPaid: form.getValues().depositPaid,
    sevenDayPaymentDate: form.getValues().sevenDayPaymentDate,
    finalPaymentDate: form.getValues().finalPaymentDate,
    additionalPaymentDate: form.getValues().additionalPaymentDate,

    // DNO application dates
    dnoApplicationSent: form.getValues().dnoApplicationSent,
    dnoApproved: form.getValues().dnoApproved,

    // Technical details date
    commissioningDate: form.getValues().commissioningDate,
  });

  // Fetch installation data directly without using TanStack Query's caching
  const [installationData, setInstallationData] = useState<Installation | null>(null);
  const [isLoadingInstallation, setIsLoadingInstallation] = useState(false);
  const [installationError, setInstallationError] = useState<Error | null>(null);
  const [isInstallationError, setIsInstallationError] = useState(false);

  // Fetch customers for dropdown with proper typing
  const { data: customers, isLoading: isLoadingCustomers } = useQuery<Customer[]>({
    queryKey: ['/api/customers'],
    enabled: true,
  });

  // Direct fetch function to get installation data
  useEffect(() => {
    const fetchInstallationData = async () => {
      if (isEditing && installationId) {
        try {
          setIsLoadingInstallation(true);
          setInstallationError(null);
          setIsInstallationError(false);

          console.log('Direct fetching installation data for ID:', installationId);
          const response = await fetch(`/api/installations/${installationId}`);

          if (!response.ok) {
            throw new Error(`Error fetching installation: ${response.statusText}`);
          }

          const data = await response.json();
          setInstallationData(data);

          // Function to deserialize document array from installation data
          const deserializeDocumentArrays = (installation: any) => {
            // Define document field names that need processing
            const documentFieldNames = [
              'dnoDocuments',
              'onsiteDocuments',
              'postInstallDocuments',
              'handoverDocuments',
              'maintainingDocuments',
              'technicalDocuments',
              'contractDocuments',
              'financeDocuments',
            ];

            // Process each document field
            for (const fieldName of documentFieldNames) {
              // Only process if field exists and contains items
              if (installation[fieldName] && Array.isArray(installation[fieldName])) {
                console.log(
                  `Deserializing ${fieldName} - original count:`,
                  installation[fieldName].length
                );

                // Process each document item
                installation[fieldName] = installation[fieldName]
                  .map((item: any, index: number) => {
                    console.log(`Raw document item ${index} type:`, typeof item);

                    // If item is already an object, convert to FileData format
                    if (typeof item === 'object' && item !== null) {
                      console.log(`Item ${index} is an object:`, item);

                      // Handle backend document format (from database)
                      if (item.id && (item.originalFilename || item.filename)) {
                        const fileData = {
                          id: item.id,
                          name: item.originalFilename || item.filename || 'Unknown document',
                          // Use filePath if available, otherwise construct API URL
                          data: item.filePath || `/api/documents/${item.id}`,
                          type: item.mimeType || item.type || 'application/octet-stream',
                          size: item.fileSize || item.size || 0,
                          // Preserve additional fields
                          filePath: item.filePath,
                          category: item.category,
                          oneDriveId: item.oneDriveId,
                        };
                        console.log(`Converted backend document to FileData:`, fileData);
                        return fileData;
                      }

                      // If it already has name property, assume it's properly formatted
                      if (item.name) {
                        console.log(`Item ${index} already has name property:`, item.name);
                        return item;
                      }

                      // Fallback for unknown object format
                      console.warn(`Unknown object format at index ${index}:`, item);
                      return null;
                    }

                    // If it's a string, create a FileData object
                    if (typeof item === 'string') {
                      try {
                        // Check if it's a data URL
                        if (item.startsWith('data:')) {
                          // Try to extract filename from data URL if present
                          let filename = `file-${index}.pdf`;
                          const filenameMatch = item.match(/;filename=([^;]+);/);
                          if (filenameMatch && filenameMatch[1]) {
                            filename = filenameMatch[1];
                          }

                          return {
                            id: `file-${index}`,
                            name: filename,
                            data: item,
                            type: item.includes('application/pdf')
                              ? 'application/pdf'
                              : 'image/jpeg',
                            size: Math.round(item.length / 1.37), // Estimate size from base64 length
                          };
                        } else {
                          // It's a filename or path
                          return {
                            id: item,
                            name: item.split('/').pop() || item,
                            data: `/api/files/${item}`, // Assume API path for retrieving the file
                            type: item.toLowerCase().endsWith('.pdf')
                              ? 'application/pdf'
                              : 'image/jpeg',
                            size: 0,
                          };
                        }
                      } catch (e) {
                        console.error(`Error processing ${fieldName} item at index ${index}:`, e);
                        return null;
                      }
                    }

                    console.warn(
                      `Unrecognized document format in ${fieldName} at index ${index}:`,
                      item
                    );
                    return null;
                  })
                  .filter(Boolean);

                console.log(`Deserialized ${fieldName} - count:`, installation[fieldName].length);
              } else {
                // Initialize as empty array if not present
                installation[fieldName] = [];
              }
            }

            return installation;
          };

          const processedData = deserializeDocumentArrays(data);
          console.log('Processed installation with deserialized documents:', processedData);

          // Function to extract and format date fields
          const prepareDateFields = (installation: any) => {
            console.log('Scheduled date from DB:', installation.scheduledDate);
            console.log('Completion date from DB:', installation.completedDate);

            // Extract date fields
            const dateFields = {
              contractSigned: installation.contractSigned
                ? new Date(installation.contractSigned)
                : null,
              scheduledDate: installation.scheduledDate
                ? new Date(installation.scheduledDate)
                : null,
              completedDate: installation.completedDate
                ? new Date(installation.completedDate)
                : null,
              depositPaid: installation.depositPaid ? new Date(installation.depositPaid) : null,
              sevenDayPaymentDate: installation.sevenDayPaymentDate
                ? new Date(installation.sevenDayPaymentDate)
                : null,
              finalPaymentDate: installation.finalPaymentDate
                ? new Date(installation.finalPaymentDate)
                : null,
              additionalPaymentDate: installation.additionalPaymentDate
                ? new Date(installation.additionalPaymentDate)
                : null,
              dnoApplicationSent: installation.dnoApplicationSent
                ? new Date(installation.dnoApplicationSent)
                : null,
              dnoApproved: installation.dnoApproved ? new Date(installation.dnoApproved) : null,
              commissioningDate: installation.commissioningDate
                ? new Date(installation.commissioningDate)
                : null,
            };

            console.log('Prepared date fields:', dateFields);
            return dateFields;
          };

          // Update date state
          const dateFields = prepareDateFields(processedData);
          setDates(dateFields);

          // Map values to form
          form.reset({
            ...processedData,
            ...dateFields,
            // Ensure ID fields are processed as numbers
            customerId: processedData.customerId ? Number(processedData.customerId) : 0,
            proposalId: processedData.proposalId ? Number(processedData.proposalId) : 0,
          });

          console.log(
            'Processing customerId:',
            processedData.customerId,
            '→',
            Number(processedData.customerId)
          );
          console.log(
            'Processing proposalId:',
            processedData.proposalId,
            '→',
            Number(processedData.proposalId)
          );
        } catch (error) {
          console.error('Error fetching installation:', error);
          setInstallationError(error as Error);
          setIsInstallationError(true);
          toast({
            title: 'Error loading installation',
            description: 'Could not load the installation details. Please try again.',
            variant: 'destructive',
          });
        } finally {
          setIsLoadingInstallation(false);
        }
      }
    };

    fetchInstallationData();
  }, [installationId, form, toast, isEditing]);

  // Create and update mutations
  const createInstallationMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      return await apiRequest({
        url: '/api/installations',
        method: 'POST',
        data,
      });
    },
    onSuccess: (data) => {
      toast({
        title: 'Installation created',
        description: 'New installation has been created successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
      window.location.href = `/installations/${data.id}`;
    },
    onError: (error) => {
      console.error('Error creating installation:', error);
      toast({
        title: 'Error',
        description: 'Failed to create installation. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateInstallationMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      try {
        console.log('UPDATE MUTATION: Starting update for installation ID:', installationId);
        console.log('UPDATE MUTATION: Form data being sent:', data);

        if (!installationId) {
          throw new Error('Installation ID is missing');
        }

        const response = await apiRequest({
          url: `/api/installations/${installationId}`,
          method: 'PATCH',
          data,
        });

        console.log('UPDATE MUTATION: Response received:', response);
        return response;
      } catch (error) {
        console.error('UPDATE MUTATION: Error occurred:', error);

        // Check if it's a network/connection error
        if (error instanceof Error) {
          console.error('UPDATE MUTATION: Error message:', error.message);
          console.error('UPDATE MUTATION: Error stack:', error.stack);

          if (
            error.message.includes('fetch') ||
            error.message.includes('network') ||
            error.message.includes('connection')
          ) {
            throw new Error(
              'Network connection error. Please check your internet connection and try again.'
            );
          }
          if (error.message.includes('timeout')) {
            throw new Error('Request timed out. Please try again.');
          }
        }

        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Installation updated successfully:', data);

      // Clear saved form data on successful update
      sessionStorage.removeItem(formDataKey);

      toast({
        title: 'Installation updated',
        description: 'Installation has been updated successfully.',
      });

      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
      queryClient.invalidateQueries({ queryKey: [`/api/installations/${installationId}`] });

      // Force a hard refresh to ensure all data is reloaded
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    onError: (error) => {
      console.error('Error updating installation:', error);

      let errorMessage = 'Failed to update installation. Please try again.';

      if (error instanceof Error) {
        if (error.message.includes('Network connection error')) {
          errorMessage = 'Network connection lost. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try saving again.';
        } else if (error.message.includes('database') || error.message.includes('connection')) {
          errorMessage = 'Database connection error. Please wait a moment and try again.';
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Helper function to serialize documents for API
  const serializeDocumentArrays = (data: FormValues) => {
    // Create a clone of the data object to avoid mutating the original
    const serializedData = { ...data };

    // Define document field names that need processing
    const documentFieldNames = [
      'dnoDocuments',
      'onsiteDocuments',
      'postInstallDocuments',
      'handoverDocuments',
      'maintainingDocuments',
      'technicalDocuments',
      'contractDocuments',
      'financeDocuments',
    ];

    // Process each document field
    for (const fieldName of documentFieldNames) {
      // Get the documents array
      const documents = serializedData[fieldName as keyof FormValues] as unknown as Array<any>;

      if (documents && Array.isArray(documents)) {
        console.log(`Processing ${fieldName} for API, count:`, documents.length);

        // For each document, check if it's already just a filename string
        // or if it needs to be serialized (has data property)
        const processedDocs = documents
          .map((doc: any) => {
            if (typeof doc === 'string') {
              return doc; // Already a filename string
            } else if (doc && doc.name) {
              if (doc.data && doc.data.startsWith('data:')) {
                // It's a new file with a data URL, needs to be sent as is
                console.log(`${fieldName} has new file:`, doc.name);
                return doc;
              } else if (doc.id) {
                // It's an existing file with an ID, send the full object to maintain metadata
                console.log(`${fieldName} has existing file with ID:`, doc.id);
                return {
                  id: doc.id,
                  name: doc.name,
                  type: doc.type || '',
                  size: doc.size || 0,
                  // Only include data if it's a data URL, otherwise leave it out to avoid huge payloads
                  ...(doc.data && doc.data.startsWith('data:') ? { data: doc.data } : {}),
                };
              } else {
                // It's an existing file without an ID, use name as fallback
                console.log(`${fieldName} has existing file without ID:`, doc.name);
                return doc.name;
              }
            } else {
              console.warn(`Invalid document in ${fieldName}:`, doc);
              return null;
            }
          })
          .filter(Boolean);

        // Use type assertion to assign processed docs back to the correct field
        (serializedData as any)[fieldName] = processedDocs;
      } else {
        // Initialize as empty array if missing or not an array
        (serializedData as any)[fieldName] = [];
      }
    }

    return serializedData;
  };

  // Form submission handler
  const onSubmit = async (data: FormValues) => {
    try {
      console.log('Starting form submission with data:', data);
      console.log('Form errors before submission:', form.formState.errors);

      // For editing, skip validation and use current form values
      if (isEditing && installationId) {
        const currentData = form.getValues();
        console.log('Using current form values for update:', currentData);
        updateInstallationMutation.mutate(currentData);
        return;
      }

      // Validate that we have the required installation ID for editing
      if (isEditing && !installationId) {
        throw new Error('Installation ID is missing for update operation');
      }

      // Fix date fields to ensure they're proper Date objects or null
      const fixedData = { ...data };

      // List of date fields that need to be converted
      const dateFields = [
        'contractSigned',
        'scheduledDate',
        'completedDate',
        'depositPaid',
        'sevenDayPaymentDate',
        'finalPaymentDate',
        'additionalPaymentDate',
        'dnoApplicationSent',
        'dnoApproved',
        'commissioningDate',
      ];

      // Convert date fields to proper Date objects or null
      for (const field of dateFields) {
        const value = (fixedData as any)[field];
        if (value === null || value === undefined || value === '') {
          (fixedData as any)[field] = null;
        } else if (typeof value === 'string') {
          // Try to parse string dates
          const parsedDate = new Date(value);
          if (isNaN(parsedDate.getTime())) {
            console.warn(`Invalid date string for ${field}: ${value}, setting to null`);
            (fixedData as any)[field] = null;
          } else {
            (fixedData as any)[field] = parsedDate;
          }
        } else if (value instanceof Date) {
          // Already a Date object, keep as is
          (fixedData as any)[field] = value;
        } else {
          // Unknown format, set to null
          console.warn(`Unknown date format for ${field}:`, value, 'setting to null');
          (fixedData as any)[field] = null;
        }
      }

      console.log(
        'Fixed date fields:',
        dateFields.reduce((acc, field) => {
          acc[field] = fixedData[field as keyof FormValues];
          return acc;
        }, {} as any)
      );

      // Prepare data with serialized document fields
      const preparedData = serializeDocumentArrays(fixedData);
      console.log('Prepared data for submission:', preparedData);

      // Show loading toast for user feedback
      toast({
        title: isEditing ? 'Updating Installation...' : 'Creating Installation...',
        description: 'Please wait while we save your changes.',
      });

      if (isEditing) {
        console.log(`FORM SUBMIT: Updating installation ${installationId}`);
        console.log('FORM SUBMIT: isEditing =', isEditing);
        console.log('FORM SUBMIT: installationId =', installationId);
        console.log('FORM SUBMIT: preparedData =', preparedData);
        console.log('FORM SUBMIT: mutation isPending =', updateInstallationMutation.isPending);

        updateInstallationMutation.mutate(preparedData);
      } else {
        console.log('FORM SUBMIT: Creating new installation');
        console.log('FORM SUBMIT: preparedData =', preparedData);

        createInstallationMutation.mutate(preparedData, {
          onSuccess: () => {
            // Clear saved form data on successful creation
            clearSavedFormData();
          },
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);

      toast({
        title: 'Submission Error',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle date changes to update both form and state
  const handleDateChange = (name: keyof typeof dates, date: Date | null) => {
    setDates((prev) => ({ ...prev, [name]: date }));
    form.setValue(name as any, date);
  };

  // Loading state
  if (isEditing && isLoadingInstallation) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/4" />
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="space-y-6 pb-20">
      {/* Header with title and back button */}
      <div className="flex items-center gap-4">
        <Link href={isEditing ? `/installations/${installationId}` : '/installations'}>
          <Button variant="outline" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Edit Installation' : 'Create New Installation'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? 'Update the details of this installation'
              : 'Configure a new solar installation project'}
          </p>
        </div>
      </div>

      {/* Main form with tabs */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 relative">
          {/* Floating save button that stays at the bottom of the screen */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t p-4 flex justify-between lg:pl-[260px]">
            <Link href={isEditing ? `/installations/${installationId}` : '/installations'}>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
            <div className="flex gap-2">
              {isEditing && installationId ? (
                <DirectUpdateButton
                  installationId={installationId}
                  formData={form.getValues()}
                  disabled={updateInstallationMutation.isPending}
                />
              ) : (
                <Button type="submit" disabled={createInstallationMutation.isPending}>
                  Create Installation
                </Button>
              )}
            </div>
          </div>

          {/* Tabs for navigating between sections */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="relative w-full overflow-hidden">
              <div className="overflow-x-auto pb-2 scrollbar-thin">
                <TabsList className="flex min-w-max w-full mb-4">
                  <TabsTrigger
                    value="details"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Info className="h-4 w-4" />
                    Basic Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="schedule"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <CalendarIcon className="h-4 w-4" />
                    Schedule & Payment
                  </TabsTrigger>
                  <TabsTrigger
                    value="dno"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Construction className="h-4 w-4" />
                    DNO Application
                  </TabsTrigger>
                  <TabsTrigger
                    value="scaffold"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Scaffold Images
                  </TabsTrigger>
                  <TabsTrigger
                    value="checklist"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    Installation Checklist
                  </TabsTrigger>
                  <TabsTrigger
                    value="technical"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Cpu className="h-4 w-4" />
                    Technical Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="notes"
                    className="flex-shrink-0 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <FileText className="h-4 w-4" />
                    Additional Notes
                  </TabsTrigger>
                </TabsList>
              </div>
              {/* Shadow indicators to show there's more content to scroll */}
              <div
                className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none opacity-0 transition-opacity duration-200 
                  peer-scroll-start:opacity-0 peer-scroll-not-start:opacity-100"
              ></div>
              <div
                className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none opacity-0 transition-opacity duration-200 
                  peer-scroll-end:opacity-0 peer-scroll-not-end:opacity-100"
              ></div>
            </div>

            {/* Basic Details Tab Content */}
            <TabsContent value="details" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Construction className="h-5 w-5" />
                    Installation Details
                  </CardTitle>
                  <CardDescription>
                    Enter the basic information about this installation project
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Customer Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <FormField
                        control={form.control}
                        name="customerId"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel>Customer</FormLabel>
                              <CustomerModal
                                onCustomerCreated={(id) => field.onChange(id)}
                                triggerText="Add Customer"
                              />
                            </div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    disabled={isLoadingCustomers}
                                    className={cn(
                                      'w-full justify-between font-normal',
                                      !field.value && 'text-muted-foreground'
                                    )}
                                  >
                                    {field.value && customers && Array.isArray(customers)
                                      ? customers.find(
                                          (customer: any) => customer.id === field.value
                                        )?.name || 'Select customer'
                                      : 'Select customer'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="p-0 w-full min-w-[300px]">
                                <Command>
                                  <CommandInput placeholder="Search customer..." className="h-9" />
                                  <CommandList>
                                    <CommandEmpty>No customer found.</CommandEmpty>
                                    <CommandGroup>
                                      {customers &&
                                        Array.isArray(customers) &&
                                        customers.map((customer: Customer) => {
                                          return (
                                            <CommandItem
                                              key={customer.id}
                                              value={customer.name || ''}
                                              onSelect={() => {
                                                field.onChange(customer.id);
                                                // Automatically populate installation address with customer address
                                                if (customer.address) {
                                                  form.setValue(
                                                    'installationAddress',
                                                    customer.address
                                                  );
                                                }

                                                // Store customer contact information in form data and state
                                                const customerData = {
                                                  id: customer.id,
                                                  name: customer.name,
                                                  email: customer.email || '',
                                                  phone: customer.phone || '',
                                                  address: customer.address || '',
                                                };

                                                // Update form values
                                                if (customer.email) {
                                                  form.setValue('customerEmail', customer.email);
                                                }
                                                if (customer.phone) {
                                                  form.setValue('customerPhone', customer.phone);
                                                }

                                                // Update state with selected customer data for display
                                                setSelectedCustomerData(customerData);

                                                // Also store in session storage for persistence
                                                sessionStorage.setItem(
                                                  `selected_customer_data`,
                                                  JSON.stringify(customerData)
                                                );

                                                console.log(
                                                  'DOMELEC DEBUG: Selected customer contact info:',
                                                  customerData
                                                );
                                              }}
                                            >
                                              {customer.name}
                                              <Check
                                                className={cn(
                                                  'ml-auto h-4 w-4',
                                                  field.value === customer.id
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                                )}
                                              />
                                            </CommandItem>
                                          );
                                        })}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <FormDescription>
                              The customer associated with this installation
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Enhanced customer contact information section */}
                      {selectedCustomerData ? (
                        <div className="mt-4 p-4 border rounded-md bg-slate-50 dark:bg-slate-800/60">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-sm">Customer Contact Information</h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                // Refresh contact information from customers query data
                                if (
                                  customers &&
                                  Array.isArray(customers) &&
                                  form.getValues().customerId
                                ) {
                                  const customerId = form.getValues().customerId;
                                  const customer = customers.find((c) => c.id === customerId);

                                  if (customer) {
                                    console.log(
                                      'DOMELEC DEBUG: Refreshing customer contact data:',
                                      customer
                                    );
                                    const customerData = {
                                      id: customer.id,
                                      name: customer.name,
                                      email: customer.email || '',
                                      phone: customer.phone || '',
                                      address: customer.address || '',
                                    };
                                    setSelectedCustomerData(customerData);

                                    // Also update session storage
                                    sessionStorage.setItem(
                                      `selected_customer_data`,
                                      JSON.stringify(customerData)
                                    );

                                    toast({
                                      title: 'Contact information refreshed',
                                      description:
                                        'The latest customer contact details have been loaded.',
                                    });
                                  }
                                }
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3.5 w-3.5 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                              </svg>
                              Refresh
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Email section */}
                            <div className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
                              <div className="flex items-center gap-2 mb-1">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-blue-500 dark:text-blue-400"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <rect width="20" height="16" x="2" y="4" rx="2" />
                                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                </svg>
                                <h5 className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                  Email Address
                                </h5>
                              </div>
                              {selectedCustomerData.email ? (
                                <div className="text-sm font-medium">
                                  {selectedCustomerData.email}
                                </div>
                              ) : (
                                <div className="text-sm text-slate-400 italic">
                                  No email address available
                                </div>
                              )}
                            </div>

                            {/* Phone section */}
                            <div className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
                              <div className="flex items-center gap-2 mb-1">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 text-green-500 dark:text-green-400"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                                <h5 className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                  Phone Number
                                </h5>
                              </div>
                              {selectedCustomerData.phone ? (
                                <div className="text-sm font-medium">
                                  {selectedCustomerData.phone}
                                </div>
                              ) : (
                                <div className="text-sm text-slate-400 italic">
                                  No phone number available
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : form.getValues().customerId ? (
                        <div className="mt-4 p-4 border rounded-md bg-slate-50 dark:bg-slate-800/60">
                          <div className="text-sm text-amber-600 flex items-center justify-between">
                            <div className="flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" x2="12" y1="8" y2="12" />
                                <line x1="12" x2="12.01" y1="16" y2="16" />
                              </svg>
                              <span>Loading customer contact information...</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => {
                                // Attempt to find customer data again
                                if (
                                  customers &&
                                  Array.isArray(customers) &&
                                  form.getValues().customerId
                                ) {
                                  const customerId = form.getValues().customerId;
                                  const customer = customers.find((c) => c.id === customerId);

                                  if (customer) {
                                    console.log(
                                      'DOMELEC DEBUG: Manually refreshing customer data:',
                                      customer
                                    );
                                    const customerData = {
                                      id: customer.id,
                                      name: customer.name,
                                      email: customer.email || '',
                                      phone: customer.phone || '',
                                      address: customer.address || '',
                                    };
                                    setSelectedCustomerData(customerData);

                                    // Also update session storage
                                    sessionStorage.setItem(
                                      `selected_customer_data`,
                                      JSON.stringify(customerData)
                                    );

                                    toast({
                                      title: 'Contact information loaded',
                                      description:
                                        'Customer contact details have been successfully loaded.',
                                    });
                                  }
                                }
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3.5 w-3.5 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                              </svg>
                              Refresh
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 p-4 border rounded-md bg-slate-50 dark:bg-slate-800/60">
                          <div className="text-sm flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-2 text-amber-500"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" x2="12" y1="8" y2="12" />
                              <line x1="12" x2="12.01" y1="16" y2="16" />
                            </svg>
                            <h4 className="font-medium">Customer Contact Information</h4>
                          </div>
                          <div className="mt-2 p-3 rounded bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
                            <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center py-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-2 text-slate-400"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                              <span>Please select a customer to view contact details</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Proposal ID - Hidden field to maintain relationship */}
                    <FormField
                      control={form.control}
                      name="proposalId"
                      render={({ field }) => (
                        <FormItem className="hidden">
                          <FormControl>
                            <Input type="hidden" {...field} value={field.value || 0} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Status and Address */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select value={field.value || undefined} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="pending">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary">Pending</Badge>
                                  <span>Not yet scheduled</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="site_survey">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-purple-500 hover:bg-purple-600">
                                    Site Survey Done
                                  </Badge>
                                  <span>Survey completed</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="ready_to_schedule">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-pink-500 hover:bg-pink-600">
                                    Ready to Schedule
                                  </Badge>
                                  <span>Ready for scheduling</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="scheduled">
                                <div className="flex items-center gap-2">
                                  <Badge>Scheduled</Badge>
                                  <span>Date confirmed</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="waiting_dno">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-yellow-500 hover:bg-yellow-600">
                                    Waiting DNO Approval
                                  </Badge>
                                  <span>DNO application submitted</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="in_progress">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-orange-500 hover:bg-orange-600">
                                    In Progress
                                  </Badge>
                                  <span>Installation started</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="completed">
                                <div className="flex items-center gap-2">
                                  <Badge variant="success">Completed</Badge>
                                  <span>Installation completed</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="cancelled">
                                <div className="flex items-center gap-2">
                                  <Badge variant="destructive">Cancelled</Badge>
                                  <span>Not proceeding</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>Current status of the installation</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="installationAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Installation Address</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Full installation address"
                            />
                          </FormControl>
                          <FormDescription>
                            The address where the system will be installed
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* System Details */}
                  <div className="grid grid-cols-1 gap-6">
                    <FormField
                      control={form.control}
                      name="systemType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>System Type</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="E.g., Solar PV, Solar + Battery"
                            />
                          </FormControl>
                          <FormDescription>Type of system being installed</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="openSolarReference"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>OpenSolar Reference</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ''}
                            placeholder="Reference ID from OpenSolar"
                          />
                        </FormControl>
                        <FormDescription>
                          Reference number from OpenSolar platform (if applicable)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Schedule & Payment Tab Content */}
            <TabsContent value="schedule" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Schedule & Payments
                  </CardTitle>
                  <CardDescription>Track key dates and payment information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={form.control}
                      name="contractSigned"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Contract Signed Date</FormLabel>
                          <DatePicker
                            date={dates.contractSigned || null}
                            setDate={(date) => handleDateChange('contractSigned', date)}
                          />
                          <FormDescription>When the customer signed the contract</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="scheduledDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Scheduled Installation Date</FormLabel>
                          <DatePicker
                            date={dates.scheduledDate || null}
                            setDate={(date) => handleDateChange('scheduledDate', date)}
                          />
                          <FormDescription>
                            When installation is scheduled to take place
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="completedDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Completion Date</FormLabel>
                          <DatePicker
                            date={dates.completedDate || null}
                            setDate={(date) => handleDateChange('completedDate', date)}
                          />
                          <FormDescription>When installation was completed</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Payment fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="installationCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total Installation Cost</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="£" />
                          </FormControl>
                          <FormDescription>Total price for the installation</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="depositAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Deposit Amount</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="£" />
                          </FormControl>
                          <FormDescription>Initial deposit amount</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="depositPaid"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Deposit Paid Date</FormLabel>
                          <DatePicker
                            date={dates.depositPaid || null}
                            setDate={(date) => handleDateChange('depositPaid', date)}
                          />
                          <FormDescription>When deposit was received</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Additional Payment Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="sevenDayPaymentAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>7-Day Payment Amount</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="£" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="sevenDayPaymentDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>7-Day Payment Date</FormLabel>
                          <DatePicker
                            date={dates.sevenDayPaymentDate || null}
                            setDate={(date) => handleDateChange('sevenDayPaymentDate', date)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="finalPaymentAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Final Payment Amount</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="£" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="finalPaymentDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Final Payment Date</FormLabel>
                          <DatePicker
                            date={dates.finalPaymentDate || null}
                            setDate={(date) => handleDateChange('finalPaymentDate', date)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Finance Documents */}
                  <div>
                    <FormField
                      control={form.control}
                      name="financeDocuments"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Finance Documents</FormLabel>
                          <FormControl>
                            <DocumentUpload
                              value={field.value}
                              onChange={field.onChange}
                              maxFiles={5}
                              accept=".pdf,.doc,.docx,.xls,.xlsx"
                            />
                          </FormControl>
                          <FormDescription>Upload finance-related documents</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Finance Documents Viewer */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Uploaded Finance Documents</h3>
                    <DocumentViewer
                      documents={form.watch('financeDocuments') || []}
                      title=""
                      emptyMessage="No finance documents have been uploaded yet"
                    />
                  </div>

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* DNO Application Tab Content */}
            <TabsContent value="dno" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    DNO Application Details
                  </CardTitle>
                  <CardDescription>
                    Track Distribution Network Operator application process
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* DNO Provider and Application Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="dnoProvider"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DNO Provider</FormLabel>
                          <Select value={field.value || ''} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="sp_energy">SP Energy Networks</SelectItem>
                              <SelectItem value="sse">SSE Networks</SelectItem>
                              <SelectItem value="ukpn">UK Power Networks</SelectItem>
                              <SelectItem value="wpd">Western Power Distribution</SelectItem>
                              <SelectItem value="northern_powergrid">Northern Powergrid</SelectItem>
                              <SelectItem value="electricity_north_west">
                                Electricity North West
                              </SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            The Distribution Network Operator for the area
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dnoApplicationType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Application Type</FormLabel>
                          <Select value={field.value || ''} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="g98">G98</SelectItem>
                              <SelectItem value="g99">G99</SelectItem>
                              <SelectItem value="g100">G100</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>Type of grid connection application</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* DNO References */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="dnoSystemReference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ENA System Reference</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="ENA reference"
                            />
                          </FormControl>
                          <FormDescription>
                            Energy Networks Association system reference
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dnoCaseReference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DNO Case Reference</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Case reference"
                            />
                          </FormControl>
                          <FormDescription>Network operator's case reference</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* DNO Application Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="dnoApplicationSent"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Application Sent Date</FormLabel>
                          <DatePicker
                            date={dates.dnoApplicationSent || null}
                            setDate={(date) => handleDateChange('dnoApplicationSent', date)}
                          />
                          <FormDescription>When the application was sent</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dnoApproved"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Approval Date</FormLabel>
                          <DatePicker
                            date={dates.dnoApproved || null}
                            setDate={(date) => handleDateChange('dnoApproved', date)}
                          />
                          <FormDescription>When application was approved</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Additional DNO Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="fastTrackReference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Fast Track Reference</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Fast track ref"
                            />
                          </FormControl>
                          <FormDescription>
                            Fast track confirmation number (if applicable)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="dnoExportLimitation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Export Limitation</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Enter export limitation"
                            />
                          </FormControl>
                          <FormDescription>Export limitation value</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Declared Net Capacity */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="dnoDeclaredNetCapacity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Declared Net Capacity (kW)</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Enter capacity in kW"
                              type="number"
                              step="0.01"
                            />
                          </FormControl>
                          <FormDescription>Declared net capacity in kilowatts</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* DNO Fees */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-medium">DNO Fees</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentFees = form.getValues('dnoFees') || [];
                          form.setValue('dnoFees', [
                            ...currentFees,
                            { description: '', amount: '' },
                          ]);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Fee
                      </Button>
                    </div>

                    {form.watch('dnoFees')?.map((fee, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start border p-3 rounded-md"
                      >
                        <div className="md:col-span-7">
                          <FormField
                            control={form.control}
                            name={`dnoFees.${index}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Fee description" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="md:col-span-4">
                          <FormField
                            control={form.control}
                            name={`dnoFees.${index}.amount`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Amount</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="£0.00" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="md:col-span-1 flex items-end justify-center pt-5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const currentFees = form.getValues('dnoFees') || [];
                              form.setValue(
                                'dnoFees',
                                currentFees.filter((_, i) => i !== index)
                              );
                            }}
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {(!form.watch('dnoFees') || form.watch('dnoFees').length === 0) && (
                      <div className="text-sm text-muted-foreground italic p-3 text-center border border-dashed rounded-md">
                        No DNO fees added yet. Click "Add Fee" to add one.
                      </div>
                    )}
                  </div>

                  {/* DNO Comments */}
                  <FormField
                    control={form.control}
                    name="dnoAdditionalComments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Comments</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ''}
                            placeholder="Additional information about the DNO application"
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Any additional notes about the DNO application process
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* DNO Documents */}
                  <div>
                    <FormField
                      control={form.control}
                      name="dnoDocuments"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DNO Documents</FormLabel>
                          <FormControl>
                            <DocumentUpload
                              value={field.value}
                              onChange={field.onChange}
                              maxFiles={10}
                              accept=".pdf,.doc,.docx"
                            />
                          </FormControl>
                          <FormDescription>
                            Upload DNO application documents, approval letters, etc.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* DNO Documents Viewer */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Uploaded DNO Documents</h3>
                    <DocumentViewer
                      documents={form.watch('dnoDocuments') || []}
                      title=""
                      emptyMessage="No DNO documents have been uploaded yet"
                    />
                  </div>

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Scaffold Images Tab Content */}
            <TabsContent value="scaffold" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Scaffold Images
                  </CardTitle>
                  <CardDescription>
                    Upload images showing where the scaffolding will be positioned at the
                    installation site
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Scaffold Image Upload Fields */}
                  <div className="grid grid-cols-1 gap-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="scaffoldImageUrl1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scaffold Image 1</FormLabel>
                            <FormControl>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      field.onChange(event.target?.result as string);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </FormControl>
                            {field.value && (
                              <div className="relative mt-2">
                                <img
                                  src={field.value}
                                  alt="Scaffold 1"
                                  className="w-full h-auto rounded-md"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2"
                                  onClick={() => field.onChange(null)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="scaffoldImageUrl2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scaffold Image 2</FormLabel>
                            <FormControl>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      field.onChange(event.target?.result as string);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </FormControl>
                            {field.value && (
                              <div className="relative mt-2">
                                <img
                                  src={field.value}
                                  alt="Scaffold 2"
                                  className="w-full h-auto rounded-md"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2"
                                  onClick={() => field.onChange(null)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="scaffoldImageUrl3"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scaffold Image 3</FormLabel>
                            <FormControl>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                      field.onChange(event.target?.result as string);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </FormControl>
                            {field.value && (
                              <div className="relative mt-2">
                                <img
                                  src={field.value}
                                  alt="Scaffold 3"
                                  className="w-full h-auto rounded-md"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2"
                                  onClick={() => field.onChange(null)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <FormField
                        control={form.control}
                        name="purchaseOrderNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scaffold Purchase Order Number</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value || ''}
                                placeholder="Enter PO number"
                              />
                            </FormControl>
                            <FormDescription>
                              Enter the purchase order number for the scaffold
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="cost"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cost</FormLabel>
                            <FormControl>
                              <Input {...field} value={field.value || ''} placeholder="£0.00" />
                            </FormControl>
                            <FormDescription>Enter the cost for the scaffold</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="scaffoldImageNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Scaffold Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              value={field.value || ''}
                              placeholder="Notes about scaffolding positioning and requirements"
                              rows={3}
                            />
                          </FormControl>
                          <FormDescription>
                            Additional notes about scaffolding requirements or positioning
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Installation Checklist Tab Content */}
            <TabsContent value="checklist" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Installation Checklist
                  </CardTitle>
                  <CardDescription>Track completion of key installation tasks</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Pre-Installation Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Pre-Installation Tasks</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistBookScaffolding"
                        label="Book Scaffolding"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistLetterScaffoldInstallDate"
                        label="Letter - Scaffold/Install Date"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistPvPlannerContractors"
                        label="PV Planner - Contractors"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistHiesCreated"
                        label="HIES Created"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistDepositInvoice"
                        label="Deposit Invoice"
                      />
                    </div>
                  </div>

                  {/* Installation Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Installation Tasks</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistSiteVisitScheduled"
                        label="Site Visit Scheduled"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistMpanRequested"
                        label="MPAN Requested"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistExportMpanReceived"
                        label="Export MPAN Received"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistSwitchExportConnect"
                        label="Switch Export on CONNECT App"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistDnoCommissioningSent"
                        label="DNO Commissioning Sent"
                      />
                    </div>
                  </div>

                  {/* Documentation Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Documentation</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistLaminateSchematic"
                        label="Laminate Schematic"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistLaminateStartupShutdown"
                        label="Laminate Startup/Shutdown"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistF17ContractManagement"
                        label="F17 - Contract Management"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistConsolidatedForm"
                        label="Consolidated Forms"
                      />
                    </div>
                  </div>

                  {/* Post-Installation Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Post-Installation</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistScaffoldRemoved"
                        label="Scaffold Removed"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistSendSunsynkApp"
                        label="Send Sunsynk CONNECT App"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistAmendmentsProposalInvoice"
                        label="Amendments to Proposal/Invoice"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistHiesReturnCompleted"
                        label="HIES Return Completed"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistPartPCertificateCreated"
                        label="Part P Certificate DP EIC Created"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistMcsCertification"
                        label="MCS Certification"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistFinancePhoenixPortal"
                        label="Finance Taken - Phoenix Portal"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistInverterSerialToStock"
                        label="Inverter Serial Number to Stock"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistFinalPaymentReceived"
                        label="Final Payment Received"
                      />
                    </div>
                  </div>

                  {/* Handover Documents Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Handover Documents</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistMcsCertificateProvided"
                        label="MCS Certificate Provided"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistEicCertificateProvided"
                        label="EIC Certificate Provided"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistPartPBuildingRegsCertificate"
                        label="Part P/Building Regs Certificate"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistOwnerManual"
                        label="Owner Manual"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistStructuralReport"
                        label="Structural Report"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistProposalUpdated"
                        label="Proposal Updated (If Install Differed)"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistSchematic"
                        label="Schematic"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistSolarPanelDetails"
                        label="Solar Panel Product Details"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistSolarPvFormsConsolidated"
                        label="Solar PV Forms Consolidated"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistInverterWarranty"
                        label="Inverter Extended Warranty"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistFinalInvoice"
                        label="Final Invoice(s)"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistCompleteOpenSolar"
                        label="Complete Job in Open Solar"
                      />
                    </div>
                  </div>

                  {/* Maintaining Tasks */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Maintaining</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ChecklistItemField
                        control={form.control}
                        name="checklistCustomerSatisfactionSurvey"
                        label="Customer Satisfaction Survey"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistDiariseYearCheck"
                        label="Diarise 1 Year Check"
                      />

                      <ChecklistItemField
                        control={form.control}
                        name="checklistAddSerialNumbersStock"
                        label="Add Serial Numbers to Stock Report"
                      />
                    </div>
                  </div>

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Technical Details Tab Content */}
            <TabsContent value="technical" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="h-5 w-5" />
                    Technical Details
                  </CardTitle>
                  <CardDescription>System technical information and identifiers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="mpanNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>MPAN Number</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="MPAN number" />
                          </FormControl>
                          <FormDescription>Meter Point Administration Number</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="exportMpan"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Export MPAN</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="Export MPAN" />
                          </FormControl>
                          <FormDescription>
                            Export Meter Point Administration Number
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="inverterSerialNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Inverter Serial Number</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="Inverter S/N"
                            />
                          </FormControl>
                          <FormDescription>Serial number of the installed inverter</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="batterySerialNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Battery Serial Number</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder="Battery S/N" />
                          </FormControl>
                          <FormDescription>Serial number of the installed battery</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="commissioningDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Commissioning Date</FormLabel>
                        <DatePicker
                          date={dates.commissioningDate || null}
                          setDate={(date) => handleDateChange('commissioningDate', date)}
                        />
                        <FormDescription>Date when the system was commissioned</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="installerNames"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Installer Names</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ''}
                            placeholder="Names of installation team"
                          />
                        </FormControl>
                        <FormDescription>Names of the installation team members</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Technical Documents */}
                  <div>
                    <FormField
                      control={form.control}
                      name="technicalDocuments"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Technical Documents</FormLabel>
                          <FormControl>
                            <DocumentUpload
                              value={field.value}
                              onChange={field.onChange}
                              maxFiles={10}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                          </FormControl>
                          <FormDescription>
                            Upload technical documents, manuals, specifications, etc.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Technical Documents Viewer */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Uploaded Technical Documents</h3>
                    <DocumentViewer
                      documents={form.watch('technicalDocuments') || []}
                      title=""
                      emptyMessage="No technical documents have been uploaded yet"
                    />
                  </div>

                  {/* Battery Serial Numbers Component */}
                  {isEditing && installationId && (
                    <div className="mt-6">
                      <h3 className="text-lg font-medium mb-4">Battery Serial Numbers</h3>
                      <BatterySerialNumbers installationId={installationId} />
                    </div>
                  )}

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Additional Notes Tab Content */}
            <TabsContent value="notes" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Additional Notes
                  </CardTitle>
                  <CardDescription>
                    Special instructions and customer-specific requirements
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/30 p-4 rounded-lg mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Quick Tip</h3>
                    <p className="text-sm">
                      Use this section to document any customer-specific instructions, access
                      information, or special considerations that the installation team should be
                      aware of.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Installation Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value || ''}
                            placeholder="Enter any additional notes or information about this installation"
                            rows={8}
                            className="min-h-[200px]"
                          />
                        </FormControl>
                        <FormDescription>
                          Add specific details about customer preferences, access requirements, or
                          special instructions
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Bottom tab navigation */}
                  <TabNavigation />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    </div>
  );
}
